/*

  GamePromptDialogs.ts

  Game action prompt dialogs component (resign, pass, exchange, challenge)

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView } from "./types";
import { ComponentFunc, m, Vnode } from "./mithril";
import { glyph, nbsp } from "./util";
import { mt, ts } from "./i18n";

interface IAttributes {
  view: IView;
}

export const GamePromptDialogs: ComponentFunc<IAttributes> = (initialVnode) => {

  // Show prompt dialogs below game board for various game actions

  const { view } = initialVnode.attrs;
  const { model } = view;

  return {

    view: () => {
      const { game } = model;
      const r: Vnode[] = [];

      if (!game || game.showingDialog === null && !game.last_chall)
        return r;

      // The dialogs below, specifically the challenge and pass
      // dialogs, have priority over the last_chall dialog - since
      // they can be invoked while the last_chall dialog is being
      // displayed. We therefore allow them to cover the last_chall
      // dialog. On mobile, both dialogs are displayed simultaneously.

      if (game.last_chall && game.localturn) {
        r.push(m(".chall-info",
          [
            glyph("info-sign"), nbsp(),
            // "Your opponent emptied the rack - you can challenge or pass"
            mt("span.pass-explain", "opponent_emptied_rack")
          ]
        ));
      }

      if (game.showingDialog === "resign") {
        r.push(m(".resign",
          [
            glyph("exclamation-sign"), nbsp(), ts("Viltu gefa leikinn?"), nbsp(),
            m("span.mobile-break", m("br")),
            m("span.yesnobutton", { onclick: () => game.confirmResign(true) },
              [glyph("ok"), ts(" Já")]
            ),
            m("span.mobile-space"),
            m("span.yesnobutton", { onclick: () => game.confirmResign(false) },
              [glyph("remove"), ts(" Nei")]
            )
          ]
        ));
      }

      if (game.showingDialog === "pass") {
        if (game.last_chall) {
          r.push(m(".pass-last",
            [
              glyph("forward"), nbsp(), ts("Segja pass?"),
              mt("span.pass-explain", "Viðureign lýkur þar með"),
              nbsp(),
              m("span.mobile-break", m("br")),
              m("span.yesnobutton", { onclick: () => game.confirmPass(true) },
                [glyph("ok"), ts(" Já")]
              ),
              m("span.mobile-space"),
              m("span.yesnobutton", { onclick: () => game.confirmPass(false) },
                [glyph("remove"), ts(" Nei")]
              )
            ]
          ));
        } else {
          r.push(m(".pass",
            [
              glyph("forward"), nbsp(), ts("Segja pass?"),
              mt("span.pass-explain", "2x3 pöss í röð ljúka viðureign"),
              nbsp(), m("span.mobile-break", m("br")),
              m("span.yesnobutton", { onclick: () => game.confirmPass(true) },
                [glyph("ok"), ts(" Já")]
              ),
              m("span.mobile-space"),
              m("span.yesnobutton", { onclick: () => game.confirmPass(false) },
                [glyph("remove"), ts(" Nei")]
              )
            ]
          ));
        }
      }

      if (game.showingDialog == "exchange") {
        r.push(m(".exchange",
          [
            glyph("refresh"), nbsp(),
            ts("Smelltu á flísarnar sem þú vilt skipta"), nbsp(),
            m("span.mobile-break", m("br")),
            m("span.yesnobutton",
              { title: ts('Skipta'), onclick: () => game.confirmExchange(true) },
              glyph("ok")
            ),
            m("span.mobile-space"),
            m("span.yesnobutton",
              { title: ts('Hætta við'), onclick: () => game.confirmExchange(false) },
              glyph("remove"))
          ]
        ));
      }

      if (game.showingDialog == "chall") {
        r.push(m(".chall",
          [
            glyph("ban-circle"), nbsp(), ts("Véfengja lögn?"),
            mt("span.pass-explain", "Röng véfenging kostar 10 stig"), nbsp(),
            m("span.mobile-break", m("br")),
            m("span.yesnobutton",
              { onclick: () => game.confirmChallenge(true) },
              [glyph("ok"), ts(" Já")]
            ),
            m("span.mobile-space"),
            m("span.yesnobutton",
              { onclick: () => game.confirmChallenge(false) },
              [glyph("remove"), ts(" Nei")]
            )
          ]
        ));
      }

      return r;
    }
  };

};
